import React, { useState } from 'react';
import moment from 'moment';
import style from './style.module.scss';
import { IHandlers, IProps } from './types';
import { Alert } from '@material-ui/lab';
import MuiAlert from '@material-ui/lab/Alert';
import { Button, Card, Theme, Typography, withStyles } from '@material-ui/core';
import MuiCardContent from '@material-ui/core/CardContent';
import MuiIconButton from '@material-ui/core/IconButton';
import MuiTypography from '@material-ui/core/Typography';
import ReactUtilsService, { renderIf } from 'external-services/react-utils-service/index';
import classNames from 'classnames';
import TableSeater from 'external-components/table-seater/alt-version';
import {
  IHandlers as ITableSeaterHandlers,
  IProps as ITableSeaterProps,
} from 'external-components/table-seater/types';
import { keys } from 'ts-transformer-keys';
import TimeSelector from 'external-components/time-selector';
import TimerIcon from '@material-ui/icons/Timer';
import TimerOffIcon from '@material-ui/icons/TimerOff';
import { makeStyles } from '@material-ui/core/styles';
import IconsService from 'external-services/icons-service/icons.service';
import { IconTypes } from 'external-services/icons-service/icons.types';
import SeatTimeButton from './SeatTimeButton';
import { APP_COLORS, Colors } from 'design-v2/globals/colors/colors';

const NS = 'StandbyTableSeater';

const IconButton = withStyles({
  root: {
    color: 'white',
    marginRight: 5,
  },
})(MuiIconButton);

const Heading = withStyles({
  root: {
    fontSize: '1.5rem',
    color: 'white',
    margin: '0',
  },
})(MuiTypography);

const CardContent = withStyles({
  root: {
    '&:last-child': {
      paddingBottom: 16,
    },
  },
})(MuiCardContent);

const WarnAlert = withStyles({
  message: {
    display: 'block',
  },
})(MuiAlert);

const StandbyIcon = IconsService.getIcon(IconTypes.standbyList);

export default function StandbyTableSeater(props: IProps & IHandlers) {
  const {
    errorMsg,
    isLoading,
    selectedTime,
    times,
    isSelectionValid,
    isFlexibleTime,
    isOffine,
    customerPhone,
    priceExistsAndChanged,
    handleSendClick,
    handleTimeChanged,
    handleSeatNow,
    handleCancelBooking,
    isLockedBooking,
  }: IProps & IHandlers = props;

  // gets just props & handlers for TableSeater
  const tableSeaterPropsKeys: string[] = keys<ITableSeaterProps & ITableSeaterHandlers>();
  const tableSeaterProps = ReactUtilsService.getObjectByKeys(
    props,
    tableSeaterPropsKeys
  ) as ITableSeaterProps & ITableSeaterHandlers;

  const isSeatingBtnsDisabled: boolean =
    isLockedBooking || isLoading || !isSelectionValid || priceExistsAndChanged;

  const formattedTime: string = moment(selectedTime).format('hh:mma');
  const selectedTimeIndex: number = times.findIndex(
    ({ time }) => formattedTime === moment(time).format('hh:mma')
  );
  const [isCustomTime, setIsCustomTime] = useState<boolean>(selectedTimeIndex === -1);

  const useStyles = makeStyles((theme: Theme) => ({
    card: {
      backgroundColor: Colors.get(APP_COLORS.clrStandby),
    },
  }));
  const classes = useStyles();

  return (
    <div className={style.root}>
      <Card className={classes.card}>
        <CardContent>
          <div className={style.headingRow}>
            <StandbyIcon className={style.standbyIcon} />
            <Heading variant="h3">Standby Booking</Heading>
          </div>

          {renderIf(tableSeaterProps.sectionsLookup, () => (
            <TableSeater
              {...tableSeaterProps}
              isWhiteTheme
              useReversedColors={true}
              showUnderline
              isLockedBooking={isLockedBooking}
            />
          ))}

          <div className={style.resendBtnWrap}>
            <Button
              data-testid="seat-confirm-btn"
              className={classNames({
                [style.btn]: true,
                [style.btnIsDisabled]: isSeatingBtnsDisabled,
              })}
              variant="outlined"
              color="default"
              size="medium"
              disabled={isSeatingBtnsDisabled}
              onClick={handleSendClick}
            >
              {renderIf(
                isOffine,
                () => (
                  <>Seat with manual confirmation</>
                ),
                () => (
                  <>Seat &amp; send confirmation</>
                )
              )}
            </Button>

            <SeatTimeButton
              selectedTime={selectedTime}
              isDisabled={isSeatingBtnsDisabled}
              handleSeatNow={() => handleSeatNow()}
            />
          </div>

          <div className={style.timeRow}>
            {renderIf(times, () => (
              <div>
                <div className={style.timeWrap}>
                  <IconButton
                    disabled={isLockedBooking}
                    aria-label="Custom time"
                    onClick={() => setIsCustomTime(!isCustomTime)}
                  >
                    {isCustomTime ? <TimerOffIcon /> : <TimerIcon />}
                  </IconButton>
                  <TimeSelector
                    isLockedBooking={isLockedBooking}
                    times={times}
                    preselectedTime={isCustomTime ? selectedTime : null}
                    preselectedTimeIndex={isCustomTime ? -1 : selectedTimeIndex}
                    isCustomTime={isCustomTime}
                    useReversedColors={true}
                    handleSelectedChanged={handleTimeChanged}
                  />
                </div>
                {renderIf(isFlexibleTime, () => (
                  <Typography className={style.flexibleTimeText}>
                    NB: Customer is flexible with time
                  </Typography>
                ))}
              </div>
            ))}

            <div className={style.cancelBtnWrap}>
              <Button
                disabled={isLockedBooking}
                className={style.btn}
                variant="outlined"
                color="default"
                size="medium"
                onClick={handleCancelBooking}
              >
                Cancel Booking
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {renderIf(priceExistsAndChanged, () => (
        <div className={style.errorWrap} data-testid="payment-msg">
          <Alert severity="error" variant="filled" className={style.errorMsg}>
            Payment details have been modified. Please save and reopen.
          </Alert>
        </div>
      ))}

      {renderIf(errorMsg, () => (
        <div className={style.errorWrap} data-testid="error-msg">
          <Alert severity="error" variant="filled" className={style.errorMsg}>
            {errorMsg}
          </Alert>
        </div>
      ))}

      {renderIf(isOffine, () => (
        <div className={style.errorWrap}>
          <WarnAlert severity="warning" variant="filled" className={style.warnMsg}>
            Cannot send standby confirmation while offline. <br />
            Operator will need to phone or SMS the customer manually to get confirmation. <br />
            Customer Ph: <a href={`tel:${customerPhone.split(' ').join('')}`}>{customerPhone}</a>
          </WarnAlert>
        </div>
      ))}
    </div>
  );
}
